import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import api from '../api/axios';
import { Report } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  report: Report;
  customerEmail?: string;
}

export default function NotifyCustomerModal({ open, onClose, report, customerEmail }: Props) {
  const facility = report.facility_name || report.ccn;

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Reset defaults when modal opens
  useEffect(() => {
    if (open) {
      setTitle(`Your report is ready: ${facility}`);
      setBody(`Your financial and staffing analysis for ${facility} (CCN: ${report.ccn}) has been completed and is ready for review.`);
      setToEmail(customerEmail || '');
      setSent(false);
      setError('');
    }
  }, [open, facility, report.ccn, customerEmail]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim());

  const handleSend = async () => {
    if (!title.trim() || !body.trim() || !emailValid) return;
    setSending(true);
    setError('');
    try {
      await api.post(`/reports/${report.uuid}/notify-customer/`, {
        title: title.trim(),
        body: body.trim(),
        to_email: toEmail.trim(),
      });
      setSent(true);
    } catch {
      setError('Failed to send notification. Please try again.');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#102B3D' }}>
        Notify Customer
      </DialogTitle>
      <DialogContent>
        {sent ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Notification sent and email delivered.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="To (email)"
              fullWidth
              required
              size="small"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              type="email"
              error={toEmail.length > 0 && !emailValid}
              helperText={toEmail.length > 0 && !emailValid ? 'Enter a valid email address' : 'Required — notification is sent only to this address'}
            />
            <TextField
              label="Subject"
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: '#6B7280' }}>
          {sent ? 'Close' : 'Cancel'}
        </Button>
        {!sent && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={sending || !title.trim() || !body.trim() || !emailValid}
            sx={{ bgcolor: '#102B3D', '&:hover': { bgcolor: '#0C2230' } }}
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
